/**
 * Language Learning Service
 * Handles all communication with the backend language learning APIs
 * Including Luna AI assistant, speech recognition, TTS, and progress tracking
 */

import toast from './toastService'
import voiceService from './voiceService'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000'
const LANGUAGE_API_URL = `${API_BASE_URL}/api/language`

class LanguageLearningService {
  /**
   * Luna AI Assistant Methods
   */

  // Chat with Luna AI assistant
  async chatWithLuna(userId, message, context = {}) {
    try {
      const response = await fetch(`${LANGUAGE_API_URL}/luna/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          message: message,
          context: context
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to chat with Luna')
      }

      return {
        success: true,
        lunaResponse: data.luna_response,
        sessionId: data.conversation_id,
        context: data.context
      }
    } catch (error) {
      console.error('Luna chat error:', error)
      toast.error('Could not connect to Luna. Please try again!')
      return {
        success: false,
        error: error.message,
        lunaResponse: "I'm having trouble right now. Please try again! 😊"
      }
    }
  }

  // Get Luna's learning tips
  async getLunaTips(userId, level = 'beginner') {
    try {
      const response = await fetch(`${LANGUAGE_API_URL}/luna/tips/${userId}?difficulty=${level}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get Luna tips')
      }

      return {
        success: true,
        tips: data.tips
      }
    } catch (error) {
      console.error('Luna tips error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Word and Learning Methods
   */

  // Get practice words
  async getWords(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString()
      const response = await fetch(`${LANGUAGE_API_URL}/words?${queryParams}`)
      
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load words')
      }

      return {
        success: true,
        // Backend returns a raw array; support both shapes
        words: Array.isArray(data) ? data : (data.words || [])
      }
    } catch (error) {
      console.error('Words loading error:', error)
      toast.error('Could not load practice words')
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Get user progress
  async getProgress(userId) {
    try {
      const response = await fetch(`${LANGUAGE_API_URL}/progress/${userId}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load progress')
      }

      return {
        success: true,
        // Backend returns a flat object with progress fields
        progress: data
      }
    } catch (error) {
      console.error('Progress loading error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Speech Recognition and TTS Methods
   */

  // Record audio and get speech recognition
  async recognizeSpeech(audioBlob, language = 'en-US') {
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')
      formData.append('language', language)

      const response = await fetch(`${LANGUAGE_API_URL}/speech/recognize`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Speech recognition failed')
      }

      return {
        success: true,
        transcription: data.transcription,
        language: data.language,
        confidence: data.confidence
      }
    } catch (error) {
      console.error('Speech recognition error:', error)
      toast.error('Could not understand the audio. Please try again!')
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Validate pronunciation
  async validatePronunciation(audioBlob, userId, wordId, targetWord) {
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'pronunciation.webm')
      formData.append('user_id', userId.toString())
      formData.append('word_id', wordId.toString())
      formData.append('target_word', targetWord)

      const response = await fetch(`${LANGUAGE_API_URL}/pronunciation/validate`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Pronunciation validation failed')
      }

      return {
        success: true,
        analysis: data.analysis,
        score: data.score,
        feedback: data.feedback
      }
    } catch (error) {
      console.error('Pronunciation validation error:', error)
      toast.error('Could not validate pronunciation. Please try again!')
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Generate speech from text (TTS) using Gemini voice
  async generateSpeech(text, language = 'en', speed = 1.0) {
    try {
      // First try Gemini 2.5 native voice
      const geminiSuccess = await this.tryGeminiVoice(text, 'friendly', language)
      
      if (geminiSuccess) {
        return {
          success: true,
          method: 'gemini_2.5_native',
          text: text,
          voice: 'Luna Gemini Voice'
        }
      }

      // Fallback to browser TTS
      const success = await voiceService.speakAsLuna(text, {
        rate: speed
      })

      if (success) {
        return {
          success: true,
          method: 'browser_tts',
          text: text,
          voice: voiceService.selectedVoice?.name || 'Browser Voice'
        }
      } else {
        throw new Error('All TTS methods failed')
      }
    } catch (error) {
      console.error('TTS generation error:', error)
      toast.error('Could not generate speech. Please try again!')
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Try Gemini 2.5 native voice
  async tryGeminiVoice(message, emotion = 'friendly', language = 'en') {
    try {
      const response = await fetch(`${LANGUAGE_API_URL}/voice/gemini/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: message,
          emotion: emotion,
          language: language
        })
      })

      if (!response.ok) {
        return false
      }

      // Get audio blob and play it
      const audioBlob = await response.blob()
      const audioUrl = URL.createObjectURL(audioBlob)
      const audio = new Audio(audioUrl)
      
      await this.playAudio(audio)
      
      URL.revokeObjectURL(audioUrl)
      return true
      
    } catch (error) {
      console.error('Gemini voice error:', error)
      return false
    }
  }

  // Helper method to play audio with promise
  async playAudio(audio) {
    return new Promise((resolve, reject) => {
      audio.addEventListener('ended', () => {
        resolve()
      })
      
      audio.addEventListener('error', (error) => {
        reject(error)
      })
      
      audio.play().catch(reject)
    })
  }

  /**
   * Audio Recording Utilities
   */

  // Start audio recording using MediaRecorder API
  async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      })

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })

      const audioChunks = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data)
        }
      }

      return {
        mediaRecorder,
        audioChunks,
        stream
      }
    } catch (error) {
      console.error('Recording start error:', error)
      toast.error('Could not access microphone. Please check permissions.')
      throw error
    }
  }

  // Stop recording and return audio blob
  async stopRecording(mediaRecorder, audioChunks, stream) {
    return new Promise((resolve) => {
      mediaRecorder.onstop = () => {
        // MediaRecorder outputs WebM/Opus; preserve correct container type
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' })
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop())
        
        resolve(audioBlob)
      }

      mediaRecorder.stop()
    })
  }

  // Play audio from URL
  async playAudioFromUrl(audioUrl) {
    try {
      const audio = new Audio(audioUrl)
      audio.volume = 0.8
      
      return new Promise((resolve, reject) => {
        audio.addEventListener('loadeddata', () => {
          audio.play()
            .then(() => resolve(true))
            .catch(error => {
              console.error('Audio play error:', error)
              reject(error)
            })
        })
        
        audio.addEventListener('error', (error) => {
          console.error('Audio loading error:', error)
          reject(error)
        })
      })
    } catch (error) {
      console.error('Audio play error:', error)
      return false
    }
  }

  /**
   * Session Management
   */

  // Save learning session
  async saveSession(userId, sessionData) {
    try {
      const response = await fetch(`${LANGUAGE_API_URL}/session/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          ...sessionData
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save session')
      }

      return {
        success: true,
        sessionId: data.session_id
      }
    } catch (error) {
      console.error('Session save error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Luna Voice Specific Methods for Enhanced UX
   */

  // Make Luna speak a chat message with personality
  async speakLunaMessage(message, emotion = 'friendly', language = 'en') {
    try {
      // First try Gemini 2.5 native voice (primary option)
      const geminiSuccess = await this.tryGeminiVoice(message, emotion, language)
      
      if (geminiSuccess) {
        return {
          success: true,
          method: 'gemini_2.5_native',
          emotion: emotion,
          voice: 'Luna Gemini Voice'
        }
      }
      
      // Fallback to browser TTS with voice service
      
      
      // Adjust voice parameters based on emotion
      const emotionSettings = {
        friendly: { pitch: 1.3, rate: 0.95 },
        excited: { pitch: 1.4, rate: 1.0 },
        encouraging: { pitch: 1.25, rate: 0.9 },
        gentle: { pitch: 1.2, rate: 0.85 }
      }

      const settings = emotionSettings[emotion] || emotionSettings.friendly

      const success = await voiceService.speakAsLuna(message, settings)
      
      return {
        success: success,
        method: 'browser_tts_fallback',
        emotion: emotion,
        voice: voiceService.selectedVoice?.name
      }
    } catch (error) {
      console.error('Luna message speech error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Preview different voice options
  async previewVoice(voiceName) {
    try {
      const voices = voiceService.getChildFriendlyVoices()
      const voice = voices.find(v => v.name === voiceName)
      
      if (!voice) {
        throw new Error('Voice not found')
      }

      const success = await voiceService.previewVoice(
        voice.voice, 
        "Hi! I'm Luna! I love helping kids learn new words and have fun with language!"
      )

      return {
        success: success,
        voiceName: voiceName
      }
    } catch (error) {
      console.error('Voice preview error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Get available child-friendly voices
  getAvailableVoices() {
    return voiceService.getChildFriendlyVoices()
  }

  // Update voice preferences
  updateVoiceSettings(voiceName, settings) {
    voiceService.setVoicePreferences(voiceName, settings)
    return voiceService.getCurrentVoiceInfo()
  }

  // Get current voice info
  getCurrentVoiceInfo() {
    return voiceService.getCurrentVoiceInfo()
  }
}

// Export singleton instance
export default new LanguageLearningService() 